import React from 'react';
import {
  ContentfulAppType,
  useGetContentfulAppsQuery,
} from '../../../services/api/contentfulApi';

/**
 * This hook is used to manage the apps return from the Contentful CMS
 * @returns all apps, client facing apps, internal facing apps, core modules
 */
export const useContentfulApps = () => {
  const {
    data: allApps,
    isSuccess: areContentfullAppsLoaded,
  } = useGetContentfulAppsQuery();

  // client facing apps except core modules
  const clientApps = React.useMemo(() => {
    if (!allApps) return [];
    return allApps
      .filter(a => a.appsType === ContentfulAppType.Client && !a.copilotAppId)
      .sort((a, b) => {
        // Sort by isFeatured key, with true values first
        if (a.isFeatured && !b.isFeatured) return -1;
        if (!a.isFeatured && b.isFeatured) return 1;
        return 0;
      });
  }, [allApps]);

  // internal facing apps
  const internalApps = React.useMemo(() => {
    if (!allApps) return [];
    return allApps.filter(a => a.appsType === ContentfulAppType.Internal);
  }, [allApps]);

  // default core modules
  const coreModules = React.useMemo(() => {
    if (!allApps) return [];
    return allApps.filter(a => a.copilotAppId);
  }, [allApps]);

  /**
   *  Method to get the single app info using app name
   * @param appName title of the app
   */
  const getAppInfo = (appName: string) =>
    clientApps.find(a => a.name === appName);

  /**
   *  Method to get the single core module info using app id
   * @param appId id of the app
   */
  const getCoreModule = (appId: string) =>
    coreModules.find(a => a.copilotAppId === appId);

  return {
    areContentfullAppsLoaded,
    allApps,
    clientApps,
    coreModules,
    internalApps,
    getAppInfo,
    getCoreModule,
  };
};
